import { writable } from 'svelte/store';
import TablaInstrument from './tablaInstrument';
import type PianoInstrument from './pianoInstrument';
import Voice from './voice';
import utils from '../utils';
import config from '../config';
import type MusicObject from './musicObject';
import type Pointer from './pointer';

/** determines which file type (mp3 or ogg) is supported */
const playableExtensions: string[] = [];
function getPlayableExtensions(): string[] {
  if (playableExtensions.length) return playableExtensions;
  // Check for supported media files, and adjust accordingly:
  const a = document.createElement('audio');
  // Try for a probably:
  const audioFileTypes = ['ogg', 'mp3', 'wav'];
  const definiteType = audioFileTypes.filter(
    (type) => a.canPlayType(`audio/${type}`) === 'probably'
  );
  const likelyType = audioFileTypes.find((type) => a.canPlayType(`audio/${type}`) === 'maybe');
  const playableTypePreference = utils.unique(definiteType.concat(likelyType).concat('wav'));
  // console.log('playable types: ', playableTypePreference.join(', '));
  return playableTypePreference;
}

// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## SoundBufferHolder.js
//
// Loads and caches audio files for playback by a virtual instrument.

// a boolean indicating whether each buffer is loaded
const loadStatuses = {};
let sourcesIndexCount = 0;

export interface MidiMessage {
  /** 0-1 double */
  velocity?: number;
  /** or string sound name */
  midiNumber: string | number;
  startBeat: number;
  stopBeat: number;
  startPointer?: Pointer;
  stopPointer?: Pointer;
  instrument?: PianoInstrument | TablaInstrument;
}

export interface MidiInfoObject {
  startBeat: number;
  stopBeat?: number;
}

// interface window {
//   MediaRecorder: MediaRecorder;
// }

export interface PlayOptions {
  startTime?: number;
  stopTime?: number;
  velocity?: number;
  absoluteTiming?: boolean;
  instrument?: PianoInstrument | TablaInstrument;
}

export interface PlayMusicOptions extends PlayOptions {
  tempo?: number;
}

interface PlaybackInfoDictionary {
  [midiNumberOrSoundName: string]: {
    bufferKey: string | number;
    playbackRate: number;
    decayRate: number;
  };
}

interface BuffersDictionary {
  [midiNumberOrSoundName: string]: AudioBuffer;
}

export interface SourceHolder {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  midiOrKey: number | string;
  index: number;
  startTime?: number;
  velocity?: number;
  onEnded?: { callback: Function; args: any }[];
}

export default class SoundBufferHolder {
  loadingPromise: Promise<void> = Promise.resolve();

  context: AudioContext;

  overallGainNode: GainNode;

  /** dictionary [soundname]: AudioBuffer */
  buffers: BuffersDictionary = {};

  /** unique to each sound being played or cued to play -- instances of buffers, in essence */
  sourceHolders: SourceHolder[] = [];

  /** dictionary for playback info: { buffer: string, playbackRate: number, decayRate: number} */
  playbackInfo: PlaybackInfoDictionary = {};

  callbacks: Function[] = [];

  errorCallbacks: Function[] = [];

  panicking: boolean;

  private _recording: {
    chunks?: any[];
    mediaRecorder?: MediaRecorder;
    url?: string;
  };

  constructor(context: AudioContext) {
    // web audio context:
    this.context = context;

    // A global gainNode:
    this.overallGainNode = this.context.createGain();
    this.overallGainNode.connect(this.context.destination);
  }

  /**
   * loads an object of soundname : urls into this.buffers[fileName]
   * @param sounds dictionary of soundName: url
   * @param instrumentName optional string
   * @returns {this}
   */
  loadSounds(sounds: { [soundName: string]: string }, instrumentName = '') {
    Object.keys(sounds).forEach((soundName) => {
      const urlPath = sounds[soundName];
      // If it's already succesfully loaded, don't reload it.
      const fullSoundName = instrumentName + soundName;
      if (fullSoundName in loadStatuses && loadStatuses[fullSoundName] === true) {
        return;
      }
      this.loadSound(fullSoundName, urlPath);
    });
    return this;
  }

  /**
   *  load a sound, create a buffer
   */
  loadSound(soundname: string, urlPath: string) {
    this.loaded(soundname, false);

    const newPromise = new Promise<void>((resolve, reject) => {
      fetchSound(urlPath, this.context)
        .then((audioBuffer) => {
          this.setBuffer(soundname, audioBuffer);
          this.loaded(soundname, true);
          resolve();
        })
        .catch((err) => {
          console.error(err);
          this.errorCallbacks.forEach((callback) => callback(new Error(err)));
          reject(err);
        });
    });

    this.loadingPromise = Promise.all([this.loadingPromise, newPromise]).then(() => {});
  }

  /**
   * Set the specified element in the buffers array to a specified sound
   * called when the decoded buffer is complete
   */
  private setBuffer(name: string, buffer: AudioBuffer) {
    // just remember this.buffers is actually an object.
    this.buffers[name] = buffer;
  }

  /**
   * Assigns a playback key to this.playbackInfo dictionary, along with settings for
   * playback speed, and decay rate (NOT YET IMPLEMENTED) for each. This allows the same
   * audio file to be accessed by different keys & played at different speeds.
   */
  setPlaybackInfo(
    playbackInfoKey: string | number,
    thisBuffersKey = playbackInfoKey,
    playbackRate = 1.0,
    decayRate = 0.8,
    instrumentName = ''
  ) {
    playbackInfoKey = `${instrumentName}${playbackInfoKey}`;
    thisBuffersKey = `${instrumentName}${thisBuffersKey}`;
    this.playbackInfo[playbackInfoKey] = {
      playbackRate,
      decayRate,
      bufferKey: thisBuffersKey,
    };
    return this;
  }

  /**
   *
   * @param midiOrKey
   * @param options
   * @param options.startTime
   */
  play(midiOrKey: number | string, options: PlayOptions = {}) {
    // console.log({ midiOrKey });
    if (this.context.state === 'suspended' && this.context.resume) {
      this.context.resume();
    }
    // If an instrument is specified, preface the midiOrKey with the
    // instrument name
    midiOrKey = `${midiOrKey}`;
    if (
      options.instrument &&
      !('isPianoInstrument' in options.instrument && options.instrument.isPianoInstrument())
    ) {
      midiOrKey = `${options.instrument.instrumentName}${midiOrKey}`;
    }

    // GCR TODO: Why do we divide velocity by 2?
    // if no velocity is set, assume 0.8, assign velocity to gainnode.
    const velocity = options.velocity !== undefined ? options.velocity / 2 : 0.8;

    // create a source and a gainnode, set the midi value for the source
    const toQueue = this.makeSource(midiOrKey, velocity);
    let startTime = options.startTime ? options.startTime : 0;
    if (options.absoluteTiming !== true) startTime += this.context.currentTime;
    toQueue.startTime = startTime;
    toQueue.source.start(startTime);
    this.stopWithRelease(toQueue, options.stopTime, options.absoluteTiming);
  }

  playMusic(musicObject: MusicObject, options: PlayMusicOptions = { tempo: 100, startTime: 0 }) {
    const { tempo, startTime } = options;
    let midiMessages = musicObject.getMidiMessages({ startBeat: 0 });
    let instrument = options.instrument;
    if (musicObject instanceof Voice && musicObject.instrument) instrument = musicObject.instrument;
    if (instrument instanceof TablaInstrument) {
      midiMessages = instrument.reviseMidiMessagesForPlayback(
        midiMessages,
        musicObject.tempo || tempo
      );
    }
    this.playMidiMessages(midiMessages, tempo, startTime);
    return this;
  }

  playMidiMessages(midiMessages: MidiMessage[], tempo = 100, startTime = 0) {
    const absoluteTiming = !!startTime;
    midiMessages.forEach((message) => {
      const noteStart = message.startBeat * (60 / tempo) + startTime; // beat * 1/tempo * MS
      const noteStop = message.stopBeat * (60 / tempo) + startTime;
      this.play(message.midiNumber, {
        absoluteTiming,
        startTime: noteStart,
        stopTime: noteStop,
        velocity: message.velocity,
        instrument: message.instrument,
      });
    });
    return this;
  }

  noteOn(midiOrKey: number | string, velocity: number) {
    this.play(midiOrKey, { velocity, startTime: 0 });
  }

  noteOff(midiOrKey: number | string) {
    // Stops all notes at this midiOrKey!!!
    this.sourceHolders.forEach((sourceHolder) => {
      // eslint-disable-next-line eqeqeq
      if (sourceHolder.midiOrKey == midiOrKey) {
        this.stopWithRelease(sourceHolder, 0);
      }
    });
  }

  panicSource(source: SourceHolder) {
    this.sourceHolders
      .filter((src) => src === source)
      .forEach((src) => {
        src.onEnded = [];
        try {
          src.source.stop();
        } catch (e) {
          // kluge for Safari
        }
      });
  }

  panic() {
    // Manual lock to prevent async callbacks from firing while
    // we are clearing the source list:
    this.panicking = true;

    this.sourceHolders.forEach((source) => {
      source.onEnded = [];
      try {
        source.source.stop();
      } catch (e) {
        // TODO: GCR -- this is a Kluge for Safari... or
        // perhaps there's a bigger bug related to
        // sources not being destroyed when they've stopped
        // playing... either way... this hides it.
      }
      //          this.trashSource(source.index);
    }, this);
    this.panicking = false;
  }

  private stopWithRelease(sourceObject: SourceHolder, stopTime: number, absoluteTiming?: boolean) {
    if (typeof stopTime === 'undefined') return;
    try {
      const source = sourceObject.source;
      const gain = sourceObject.gainNode.gain;
      const decay = 0.07;
      const velocity = typeof sourceObject.velocity === 'undefined' ? 0.8 : sourceObject.velocity;

      if (absoluteTiming !== true) stopTime += this.context.currentTime;
      gain.linearRampToValueAtTime(velocity, stopTime);
      gain.linearRampToValueAtTime(0, stopTime + decay);
      source.stop(stopTime + decay);
    } catch (err) {
      console.error(
        `%cError stopping sound: sound may have been stopped already by another process.${err}`,
        'background: #EECCCC;'
      );
      console.error(err);
    }
  }

  private loadStore = writable<{ loadingStatus: number }>({ loadingStatus: 100 });

  subscribe = this.loadStore.subscribe;

  /** callback when load status has changed with a new percent (0 - 100) */
  notifyLoadStatus(callback: (number) => any, thisArg?) {
    // adds function to be called back when status changes.
    callback = thisArg ? callback.bind(thisArg) : callback;

    // Add it to be notified:
    this.callbacks.push(callback.bind(thisArg));

    // And call it back right away:
    callback(this.loadStatus());
    this.loadStore.set({ loadingStatus: this.loadStatus() });
  }

  notifyError(callback: Function, thisArg?) {
    thisArg = thisArg || this;
    this.errorCallbacks.push(callback.bind(thisArg));
  }

  clearNotifiers() {
    this.callbacks = [];
    this.errorCallbacks = [];
  }

  removeNotifyLoadStatus(callback: Function) {
    // removes function to be called back when status changes
    this.callbacks.splice(this.callbacks.indexOf(callback), 1);
  }

  loadStatus() {
    // checks percent & returns it
    let loaded = 0;
    let unloaded = 0;
    Object.keys(loadStatuses).forEach((key) => {
      if (loadStatuses[key] === true) loaded++;
      else unloaded++;
    });
    const total = loaded + unloaded;
    if (total === 0) return 0;
    return Math.floor(100 * (loaded / total));
  }

  /**
   * Update the load status of a soundBuffer
   */
  private loaded(name: string, loaded: boolean) {
    // console.log("Load status of: " + name + ", " + loaded);
    loadStatuses[name] = loaded;
    const loadedpercent = this.loadStatus();
    this.callbacks.forEach((value) => {
      value(loadedpercent);
    });
  } // called when a file is loaded by loader

  toString() {
    return 'SoundBufferHolder object';
  }

  // ## Internal helper functions:

  // MakeSource
  // Makes an individual source given a key to this.playbackInfo;
  // consults playback info for playbackRate, decayRate, etc...
  private makeSource(midiNumberOrDescriptiveKey: number | string, velocity?: number): SourceHolder {
    // console.log('midiNumberOrKey', midiNumberOrDescriptiveKey);
    sourcesIndexCount++;
    const source = this.context.createBufferSource();
    const playbackInfo = this.playbackInfo[midiNumberOrDescriptiveKey];
    try {
      source.buffer = this.buffers[playbackInfo.bufferKey];
      source.playbackRate.value = playbackInfo.playbackRate;
    } catch (err) {
      console.error(`%cError creating sound buffer: ${err}`);
      console.error(err);
    }
    const index = sourcesIndexCount;

    const gainNode = this.context.createGain();
    if (velocity) {
      gainNode.gain.value = velocity;
    }
    source.connect(gainNode);
    gainNode.connect(this.overallGainNode);
    const sourceHolder: SourceHolder = {
      index,
      velocity,
      source,
      gainNode,
      midiOrKey: midiNumberOrDescriptiveKey,
    };
    this.sourceHolders.push(sourceHolder);

    source.onended = () => {
      this.trashSource(index);
    };

    return sourceHolder;
  }

  onEnded(callback: Function, thisArg?, args?) {
    callback = thisArg ? callback.bind(thisArg) : callback;
    const sourceHolder = this.sourceHolders[this.sourceHolders.length - 1];
    if (!sourceHolder.hasOwnProperty('onEnded')) {
      sourceHolder.onEnded = [];
    }
    sourceHolder.onEnded.push({ callback, args });
  }

  onStarted(callback: Function, thisArg?, arg?) {
    // Since there's no "started" or "play" event,
    // we kluge this by playing back a 0.001s audio
    // file and setting a stop event.
    //
    let sourceHolder = this.sourceHolders[this.sourceHolders.length - 1];
    const boundCallback = thisArg ? callback.bind(thisArg) : callback;

    if (
      this.sourceHolders.length === 0 ||
      sourceHolder.startTime <= this.context.currentTime + 0.05
    ) {
      boundCallback(arg);
      return;
    }
    const startTime = sourceHolder.startTime;
    sourceHolder = this.makeSource('silence');
    sourceHolder.onEnded = [{ callback: boundCallback, args: arg }];
    sourceHolder.source.start(startTime);
  }

  private trashSource(index: number) {
    const sourceHolder = this.sourceHolders.find((sh) => sh.index === index);
    if (!sourceHolder) return;
    const sourceHoldersArrayIndex = this.sourceHolders.indexOf(sourceHolder);
    if (sourceHolder.hasOwnProperty('onEnded') && !this.panicking) {
      sourceHolder.onEnded.forEach((endedObject) => {
        endedObject.callback(endedObject.args);
      });
    }
    this.sourceHolders.splice(sourceHoldersArrayIndex, 1);
  }

  enableRecording() {
    this._recording = this._recording || {};
    this._recording.chunks = [];
    if (!this._recording.mediaRecorder) {
      const dest = this.context.createMediaStreamDestination();
      const mediaRecorder = new MediaRecorder(dest.stream);
      this.overallGainNode.connect(dest);
      this._recording.mediaRecorder = mediaRecorder;
      mediaRecorder.ondataavailable = (evt) => {
        this._recording.chunks.push(evt.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new window.Blob(this._recording.chunks, { type: 'audo/webm; codecs=opus' });
        window.location.href = window.URL.createObjectURL(blob);
        console.log(`Recording found at: ${this._recording.url}`);
      };
    }
  }

  startRecording() {
    this.enableRecording();
    this._recording.mediaRecorder.start();
  }

  stopRecording() {
    this._recording.mediaRecorder.stop();
  }
}

function fetchSound(
  urlPath,
  audioContext: AudioContext,
  errors: string[] = [],
  validExtensions: string[] = getPlayableExtensions(),
  validDomains: string[] = [config.soundsURL, config.backupSoundsURL]
): Promise<AudioBuffer> {
  if (!validExtensions.length)
    return Promise.reject(`Unable to load audio for ${urlPath}, no valid extensions detected.`);
  const domain = validDomains.shift();
  const extension = validExtensions[0];
  const url = `${domain + urlPath}.${extension}`;

  return window
    .fetch(url, {
      method: 'get',
      cache: 'force-cache',
    })
    .then((response) => {
      if (!response.ok) {
        return Promise.reject(
          `${url} failed to load with response.status ${response.status}: ${response.statusText}`
        );
      }
      return response.arrayBuffer();
    })
    .then(
      (arrayBuffer) =>
        // Note: Safari 14 does not implement the promise version of this yet, so we
        // have to stick with the old success, failure callbacks
        new Promise((resolve, reject) => {
          audioContext.decodeAudioData(
            arrayBuffer,
            (audioBuffer) => resolve(audioBuffer),
            (error) => reject(error)
          );
        }) as Promise<AudioBuffer>
    )
    .catch((err) => {
      console.error(err);
      errors.push(`${err.toString()} ${urlPath}`);
      // First, try the other domain -- maybe we have a CORS error
      if (validDomains.length) {
        return fetchSound(urlPath, audioContext, errors, validExtensions, validDomains);
      }

      // Otherwise try another extension:
      if (validExtensions.length > 1) {
        validExtensions = validExtensions.slice(1);
        return fetchSound(urlPath, audioContext, errors, validExtensions);
      }
      return Promise.reject(errors.join('\n'));
    });
}
